import React from "react";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from "./ui/button";
import axios from "axios";
import './PreviewNode.css';

const PreviewNode = ({ 
  previewNode, 
  setShowPreview, 
  setPreviewNode, 
  resetForm, 
  addPendingNode, 
  nodes, 
  API_BASE_URL, 
  setLoading,
  fetchNodes,
  mode = "libre", // "libre" (default) or "funnel"
  onValidate, // for funnel mode
  onBack // for funnel mode
}) => {
  const { t } = useTranslation();
  
  if (!previewNode) return null;

  // Functions to handle different actions
  const handlePendingNode = () => {
    // Ajouter le flag explicite si ce n'est pas déjà défini et que ce n'est pas un test
    const nodeToAdd = {
      ...previewNode,
      isActualNode: previewNode.type !== 'Test' && !previewNode.isActualNode ? true : previewNode.isActualNode
    };
    addPendingNode(nodeToAdd);
    setShowPreview(false);
    setPreviewNode(null);
    resetForm();
  };

  const handleCreateNode = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/node`, previewNode);
      if (typeof fetchNodes === 'function') {
        await fetchNodes();
      }
      setShowPreview(false);
      setPreviewNode(null);
      resetForm();
    } catch (e) {
      console.error("Error during creation:", e);
      toast.error(t('previewNode.createError', { message: e.response?.data?.message || e.message }));
    }
    setLoading(false);
  };

  // --- Action buttons logic ---
  let actionButtons = null;
  if (mode === "funnel") {
    actionButtons = (
      <>
        <Button
          onClick={onValidate}
          className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 transform transition duration-200 hover:-translate-y-1"
          title={t('previewNode.validateNext')}
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">✅</span>
            <span>{t('previewNode.validateContinue')}</span>
          </div>
        </Button>
        <Button
          onClick={onBack || (() => setShowPreview(false))}
          className="bg-gray-300 hover:bg-gray-400 text-gray-800 transform transition duration-200 hover:-translate-y-1"
          title={t('previewNode.back')}
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">↩️</span>
            <span>Retour</span>
          </div>
        </Button>
      </>
    );
  } else {
    actionButtons = (
      <>
        <Button
          onClick={handleCreateNode}
          className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 transform transition duration-200 hover:-translate-y-1"
          title="Créer ce nœud directement dans la base de connaissance"
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">💾</span>
            <span>Créer ce nœud</span>
          </div>
        </Button>
        <Button
          onClick={handlePendingNode}
          className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-600 hover:to-yellow-500 transform transition duration-200 hover:-translate-y-1"
          title={t('previewNode.addPendingTitle')}
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">🧱</span>
            <span>{t('previewNode.putOnHold')}</span>
          </div>
        </Button>
        <Button
          onClick={() => setShowPreview(false)}
          className="bg-gray-300 hover:bg-gray-400 text-gray-800 transform transition duration-200 hover:-translate-y-1"
          title={t('previewNode.backToInput')}
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">✏️</span>
            <span>{t('previewNode.modify')}</span>
          </div>
        </Button>
        <Button
          onClick={() => {
            setShowPreview(false);
            setPreviewNode(null);
          }}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 transform transition duration-200 hover:-translate-y-1"
          title={t('common.cancel')}
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">❌</span>
            <span>{t('common.cancel')}</span>
          </div>
        </Button>
      </>
    );
  }

  return (
    <div className="preview-container w-full mx-auto bg-white mt-4 p-4 rounded-lg animate-fadeIn overflow-hidden">
      {/* Header avec dégradé similaire aux autres sections */}
      <div className="response-header rounded-t-lg" style={{
        background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
      }}>
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-bold text-white flex items-center">
            <span className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">👁️</span>
            Aperçu du nœud à créer
          </h3>
          <div className="bg-white bg-opacity-20 px-4 py-1 rounded-full text-white">
            {previewNode.type}
          </div>
        </div>
      </div>
      
      {/* Corps principal avec style harmonisé */}
      <div className="bg-white p-6 shadow-inner border-l border-r border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Section d'informations de base */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200 shadow-sm">
            <h4 className="text-indigo-700 font-semibold mb-3 flex items-center">
              <span className="text-indigo-500 mr-2">📋</span>
              Informations générales
            </h4>
            <div className="txtNodePreview space-y-3">
              <div className="flex">
                <span className="text-gray-500 font-medium w-32">Nom:</span>
                <span className="font-semibold text-gray-800">{previewNode.name}</span>
              </div>
              <div className="flex">
                <span className="text-gray-500 font-medium w-32">Type:</span>
                <span className="font-semibold text-gray-800">{previewNode.type}</span>
              </div>
              <div className="flex">
                <span className="text-gray-500 font-medium w-32">Technologie:</span>
                <span className="font-semibold text-gray-800">{previewNode.technology || <span className="text-gray-400 italic">Non spécifié</span>}</span>
              </div>
              <div className="flex">
                <span className="text-gray-500 font-medium w-32">Statut:</span>
                <span className="font-semibold text-gray-800">{previewNode.status || <span className="text-gray-400 italic">Non spécifié</span>}</span>
              </div>
            </div>
          </div>
          
          {/* Section de description */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200 shadow-sm">
            <h4 className="text-blue-700 font-semibold mb-3 flex items-center">
              <span className="text-blue-500 mr-2">📝</span>
              Description
            </h4>
            <div className="bg-white p-3 rounded border-l-4 border-blue-300 shadow-inner h-[calc(100%-3rem)] overflow-y-auto">
              {previewNode.description ? (
                <p className="text-gray-700">{previewNode.description}</p>
              ) : (
                <p className="text-gray-400 italic">Aucune description fournie</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Section JSON pour les métadonnées - affichée seulement si présente */}
        {previewNode.metadata && Object.keys(previewNode.metadata).length > 0 && (
          <div className="mt-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200 shadow-sm">
            <h4 className="text-purple-700 font-semibold mb-3 flex items-center">
              <span className="text-purple-500 mr-2">🔧</span>
              Métadonnées
            </h4>
            <div className="bg-gray-800 p-3 rounded-md shadow-inner overflow-x-auto">
              <pre className="text-green-400 font-mono text-sm">{JSON.stringify(previewNode.metadata, null, 2)}</pre>
            </div>
          </div>
        )}
        
        {/* Section JSON pour le schéma d'endpoints - affichée seulement si présent */}
        {previewNode.endpointSchema && Object.keys(previewNode.endpointSchema).length > 0 && (
          <div className="mt-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200 shadow-sm">
            <h4 className="text-teal-700 font-semibold mb-3 flex items-center">
              <span className="text-teal-500 mr-2">🌐</span>
              Schéma des Endpoints
            </h4>
            <div className="bg-gray-800 p-3 rounded-md shadow-inner overflow-x-auto">
              <pre className="text-green-400 font-mono text-sm">{JSON.stringify(previewNode.endpointSchema, null, 2)}</pre>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer avec boutons d'action adaptés au mode */}
      <div className="response-actions bg-gray-50 p-4 border-t border-gray-200 rounded-b-lg flex flex-wrap justify-center gap-3">
        {actionButtons}
      </div>
      
      {/* Astuce en bas de page */}
      <div className="mt-4 text-center">
        <span className="inline-block bg-blue-50 border border-blue-100 text-blue-600 text-xs px-4 py-1.5 rounded-full">
          <span className="font-bold mr-1">💡 Astuce:</span>
          Vous pouvez mettre en attente ce noeud pour préparer une validation collaborative avec votre équipe.
        </span>
      </div>
    </div>
  );
};

export default PreviewNode;